import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import theme from './theme';

// Pages
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Profile from './pages/Profile';
import Checkout from './pages/Checkout';
import Success from './pages/Checkout/Success';
import ProductDetails from './pages/ProductDetails';
import CollectionsPage from './pages/CollectionsPage';

// Import High Jewelry and Watches Pages
import HighJewelryPage from './pages/HighJewelry';
import WatchesPage from './pages/Watches';
import OurStoryPage from './pages/OurStory';
import ServicesPage from './pages/Services';
import CustomInquiriesPage from './pages/CustomInquiries';
import ContactPage from './pages/ContactPage';

// Jewelry Category Pages
import RingsPage from './pages/Jewelry/Rings';
import NecklacesPage from './pages/Jewelry/Necklaces';
import EarringsPage from './pages/Jewelry/Earrings';
import PiercingsPage from './pages/Jewelry/Piercings';
import BraceletsPage from './pages/Jewelry/Bracelets';

// Affiliate Pages
import AffiliatePage from './pages/Affiliate';
import AffiliateRegister from './pages/Affiliate/Register';
import AffiliatePending from './pages/Affiliate/Pending';
import AffiliateDashboard from './pages/Affiliate/Dashboard';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';

// Design Studio pulls in three.js (~600KB gzipped). Lazy-loading it keeps that
// weight out of the main bundle for every visitor who never opens the builder.
const DesignStudio = React.lazy(() => import('./pages/DesignStudio'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
    <CircularProgress sx={{ color: '#c4a661' }} />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <CartProvider>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              } />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<Success />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/our-story" element={<OurStoryPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/custom-inquiries" element={<CustomInquiriesPage />} />
              <Route
                path="/design"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <DesignStudio />
                  </Suspense>
                }
              />
              <Route path="/contact" element={<ContactPage />} />
              
              {/* High Jewelry and Watches Routes */}
              <Route path="/high-jewelry" element={<HighJewelryPage />} />
              <Route path="/watches" element={<WatchesPage />} />

              {/* Jewelry Category Routes */}
              <Route path="/jewelry/rings" element={<RingsPage />} />
              <Route path="/jewelry/necklaces" element={<NecklacesPage />} />
              <Route path="/jewelry/earrings" element={<EarringsPage />} />
              <Route path="/jewelry/piercings" element={<PiercingsPage />} />
              <Route path="/jewelry/bracelets" element={<BraceletsPage />} />

              {/* Affiliate Routes */}
              <Route path="/affiliate" element={<AffiliatePage />} />
              <Route path="/affiliate/register" element={
                <PrivateRoute>
                  <AffiliateRegister />
                </PrivateRoute>
              } />
              <Route path="/affiliate/pending" element={
                <PrivateRoute>
                  <AffiliatePending />
                </PrivateRoute>
              } />
              <Route path="/affiliate/dashboard/*" element={
                <PrivateRoute>
                  <AffiliateDashboard />
                </PrivateRoute>
              } />
            </Routes>
            <Footer />
          </CartProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
